<template>
  <PTransparentLoader :loading="loading">
    <template v-if="checkin">
      <DateOfStayModal :end_of_stay="dateOfStay.end" :start_of_stay="dateOfStay.start" ref="modalDateOfStay"/>
      <AssignCouponModal
          v-if="canAssignCoupon"
          ref="modalAssignCoupon"
          :user-id="checkin.user"
          :pica-service-id="picaServiceId"
          :picacode="checkin.picacode"
      />
      <CCard>
        <CCardHeader>
          <h5 class="main-header mb-1" v-translate translate-context="checkin.title">Checkin</h5>
        </CCardHeader>
        <CCardBody class="px-0">
          <CContainer fluid class="grid-stripped">
            <PFRow :label="$pgettext('checkin.label','Pica Code')">
              {{ checkin.picacode }}
            </PFRow>
            <PFRow :label="$pgettext('checkin.label','Available Photos')">
              <CLink :to="routeToSearchPhotos(checkin.picacode)">{{ checkin.available_photos_number|number }}</CLink>
            </PFRow>
            <PFRow :label="$pgettext('checkin.label','Date of Stay')">
              <CButton color="dark" class="btn-circle" variant="outline" size="sm"
                       @click="editDateOfStay"
                       v-if="hasPerm('checkin.update_checkin')"
              >
                <CIcon name="cipPencil" size="sm"></CIcon>
              </CButton>
              <div class="ml-2 d-inline" v-if="checkin.start_of_stay || checkin.end_of_stay">
                {{ checkin.start_of_stay|formatDate('LL', timeZone) }} - {{ checkin.end_of_stay|formatDate('LL', timeZone) }}
              </div>

            </PFRow>
          </CContainer>
        </CCardBody>
      </CCard>

      <CCard v-if="canAssignCoupon">
        <CCardHeader class="d-flex justify-content-between align-items-center">
          <h5 class="main-header mb-0" v-translate translate-context="checkin.coupon.title">Coupons</h5>
          <CButton color="primary" size="sm" @click="openAssignCouponModal">
            <CIcon name="cil-plus" class="mr-1"/>
            <translate translate-context="checkin.coupon.actions">Assign Coupon</translate>
          </CButton>
        </CCardHeader>
        <CCardBody class="px-0">
          <div v-if="loadingRedemptions" class="text-center py-3">
            <CSpinner color="primary" size="sm"/>
          </div>
          <template v-else-if="redemptions.length === 0">
            <p class="text-muted text-center py-3 mb-0">
              <translate translate-context="checkin.coupon">No coupons assigned yet.</translate>
            </p>
          </template>
          <CListGroup v-else flush>
            <CListGroupItem v-for="redemption in redemptions" :key="redemption.id" class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ redemption.coupon.code }}</strong>
                <br>
                <small class="text-muted">{{ redemption.coupon.batch.promotion_rule.name }}</small>
                <template v-if="getRedemptionAmount(redemption)">
                  <br>
                  <small class="text-muted">{{ getRedemptionAmount(redemption) | currency(eventCurrency) }}</small>
                </template>
              </div>
              <div class="text-right">
                <CBadge :color="redemption.is_consumed ? 'secondary' : 'success'">
                  {{ redemption.is_consumed ? $pgettext('coupon.status', 'Used') : $pgettext('coupon.status', 'Active') }}
                </CBadge>
                <br>
                <small class="text-muted">{{ redemption.redeemed_at | formatDate('lll', timeZone) }}</small>
              </div>
            </CListGroupItem>
          </CListGroup>
        </CCardBody>
      </CCard>
    </template>
  </PTransparentLoader>
</template>
<script>
import {mapActions, mapGetters} from "vuex";
import PFRow from "@/domain/core/components/PFRow.vue";
import PTransparentLoader from "@/domain/core/components/PTransparentLoader.vue";
import DateOfStayModal from "@/domain/checkin/components/DateOfStayModal.vue";
import AssignCouponModal from "@/domain/edgeCoupon/components/AssignCouponModal.vue";
import moment from "moment";
import ConfigScope from "@/domain/core/constant/configScope";

export default {
  name: "Checkin",
  components: {DateOfStayModal, AssignCouponModal, PTransparentLoader, PFRow},
  data() {
    return {
      loading: true,
      start_of_stay: '',
      end_of_stay: '',
    }
  },
  computed: {
    ...mapGetters('event', [
      'picaServiceId',
      'timeZone',
      'isEdge',
      'eventCurrency'
    ]),
    ...mapGetters('checkin', [
      'checkin',
    ]),
    ...mapGetters('features', [
      'isCouponEnabled'
    ]),
    ...mapGetters('login', [
      'hasPerm'
    ]),
    ...mapGetters('edgeCoupon', {
      redemptions: 'redemptions',
      couponLoading: 'loading'
    }),
    loadingRedemptions() {
      return this.couponLoading?.redemptions || false
    },
    canAssignCoupon() {
      return this.isCouponEnabled && this.isEdge && this.hasPerm('checkin.coupon.assign')
    },
    dateOfStay() {
      return {
        start: moment.tz(this.checkin.start_of_stay, this.timeZone).format('YYYY-MM-DD'),
        end: moment.tz(this.checkin.end_of_stay, this.timeZone).format('YYYY-MM-DD')
      }
    }
  },
  async mounted() {
    this.loading = true
    this.setPicaServiceId(this.picaServiceId)
    this.setCheckinId(this.$route.params.id)
    await this.fetchConfig({type: 'checkin', id: this.picaServiceId, scope: ConfigScope.PICA_SERVICE})
    await this.fetchCheckin()
    if (this.canAssignCoupon && this.checkin?.user) {
      await this.fetchRedemptions({ userId: this.checkin.user, picacode: this.checkin.picacode })
    }
    this.loading = false
  },
  methods: {
    ...mapActions('login', [
      'fetchConfig'
    ]),
    ...mapActions('checkin', [
      'fetchCheckin',
      'setPicaServiceId',
      'setCheckinId',
    ]),
    ...mapActions('edgeCoupon', [
      'fetchRedemptions'
    ]),
    routeToSearchPhotos(picacode) {
      return {name: 'MediaSearch', params: {picaServiceId: this.picaServiceId}, query: {picacode: picacode}}
    },
    editDateOfStay() {
      this.$refs.modalDateOfStay.open()
    },
    openAssignCouponModal() {
      this.$refs.modalAssignCoupon.open()
    },
    getRedemptionAmount(redemption) {
      if (redemption.effect_params_overrides?.amount !== undefined) {
        return redemption.effect_params_overrides.amount
      }
      return redemption.coupon?.batch?.promotion_rule?.effect_params?.amount || null
    }
  }
}
</script>